import React, { useMemo, useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import useAutoRefresh from "@/lib/useAutoRefresh";
import LastUpdatedChip from "@/components/LastUpdatedChip";
import ExportButton from "@/components/ExportButton";
import JumpToLatestButton from "@/components/JumpToLatestButton";
import useLedgerSortPref from "@/hooks/useLedgerSortPref";
import { toast } from "sonner";
import { playDing } from "@/lib/audio";
import { CheckCircle, ArrowSquareUpRight, XCircle, ArrowUp, ArrowDown } from "@phosphor-icons/react";
import TxnDateCell from "@/components/TxnDateCell";

const inr = (n) => (n ? new Intl.NumberFormat("en-IN", { minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(n) : "");
const inr0 = (n) => (n ? new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(Math.round(n)) : "");
const inrCur = (n) => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR" }).format(n || 0);
const formatDate = (iso) => {
    if (!iso) return "—";
    const s = String(iso);
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
    if (m) return `${m[3]}/${m[2]}/${m[1]}`;
    const d = new Date(s);
    if (Number.isNaN(d.getTime())) return iso;
    const p = (n) => String(n).padStart(2, "0");
    return `${p(d.getDate())}/${p(d.getMonth() + 1)}/${d.getFullYear()}`;
};

export default function CustomerLedger() {
    const { user } = useAuth();
    // v1.20.5 — auto-refresh every 30s + on focus / visibility change so admin
    // updates (new payments, corrections, payouts) reflect in the customer's
    // view without them having to reload manually.
    const { data: ledger, lastUpdated, refresh, isRefreshing } = useAutoRefresh(
        () => user?.party_id ? api.get(`/ledger/${user.party_id}`).then((r) => r.data) : Promise.resolve(null),
        { deps: [user?.party_id], intervalMs: 30_000 },
    );

    // v1.20.6 — pulse-toast: whenever an auto-refresh brings in a NEW row
    // that wasn't in the previous snapshot, fire a celebratory toast + audio
    // ding so the customer sees their balance change in real time. First
    // snapshot is used only as a baseline (no toast fired on load).
    const seenIdsRef = useRef(null);
    useEffect(() => {
        if (!ledger?.rows) return;
        const currentIds = new Set(ledger.rows.map((r) => r.id));
        if (seenIdsRef.current === null) {
            // Baseline the first snapshot silently.
            seenIdsRef.current = currentIds;
            return;
        }
        const prev = seenIdsRef.current;
        const newRows = ledger.rows.filter((r) => !prev.has(r.id));
        if (newRows.length > 0) {
            for (const r of newRows) {
                const amt = Number(r.amount || 0);
                const inr = new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(amt);
                if (r.txn_type === "payment") {
                    toast.success(`New payment approved · ${inr} added`, {
                        description: r.utr ? `UTR ${r.utr}` : undefined,
                        icon: <CheckCircle size={18} weight="fill" className="text-emerald-600" />,
                    });
                } else if (r.txn_type === "payout") {
                    toast.info(`New payout recorded · ${inr}`, {
                        description: r.reference ? r.reference.toUpperCase() : undefined,
                        icon: <ArrowSquareUpRight size={18} weight="bold" className="text-violet-700" />,
                    });
                } else if (r.txn_type === "invoice") {
                    toast(`New invoice · ${inr}`, {
                        description: r.reference || undefined,
                    });
                } else if (r.txn_type === "commission" || r.txn_type === "portal_fee") {
                    // charges are attached to payments; skip a separate toast
                    continue;
                } else {
                    toast(`New entry · ${inr}`);
                }
            }
            // Play the ding once even if multiple rows arrived at once.
            const hasSignificant = newRows.some((r) => r.txn_type !== "commission" && r.txn_type !== "portal_fee");
            if (hasSignificant) playDing();
        }
        seenIdsRef.current = currentIds;
    }, [ledger]);

    const displayRows = useMemo(() => {
        if (!ledger) return [];
        const commissionByKey = new Map();
        const portalFeeByKey = new Map();
        ledger.rows.forEach((r) => {
            const key = r.payment_txn_id || r.upload_id;
            if (!key) return;
            if (r.txn_type === "commission") commissionByKey.set(key, r);
            else if (r.txn_type === "portal_fee") portalFeeByKey.set(key, r);
        });
        const out = [];
        let bal = Number(ledger.opening_balance || 0);
        for (const r of ledger.rows) {
            if (r.txn_type === "commission" || r.txn_type === "portal_fee") continue;
            bal += Number(r.amount_signed || 0);
            let recd = 0, pct = 0, pctAmt = 0, fixAmt = 0, netAmt = 0, paid = 0;
            let remark = "";
            if (r.txn_type === "payment") {
                recd = Number(r.amount || 0);
                const linkedComm = commissionByKey.get(r.id) || commissionByKey.get(r.upload_id);
                const linkedFee  = portalFeeByKey.get(r.id) || portalFeeByKey.get(r.upload_id);
                if (linkedComm) {
                    bal += Number(linkedComm.amount_signed || 0);
                    pct = Number(linkedComm.charge_percent || 0);
                    pctAmt = Number(linkedComm.amount || 0);
                }
                if (linkedFee) {
                    bal += Number(linkedFee.amount_signed || 0);
                    fixAmt = Number(linkedFee.amount || 0);
                }
                if (linkedComm && !linkedFee && (linkedComm.is_flat_fee || (linkedComm.description || "").startsWith("Portal charge"))) {
                    fixAmt = pctAmt;
                    pctAmt = 0;
                    pct = 0;
                }
                netAmt = recd - pctAmt;
                remark = "APPROVED";
            } else if (r.txn_type === "payout") {
                paid = Number(r.amount || 0);
                remark = r.reference ? r.reference.toUpperCase() : "PAID";
            } else if (r.txn_type === "invoice") {
                recd = Number(r.amount || 0);
                remark = "INVOICE";
            } else {
                recd = Number(r.amount || 0);
                remark = String(r.txn_type || "").toUpperCase();
            }
            out.push({
                id: r.id, date: r.date,
                utr: r.utr || r.reference || "",
                recd, pct, pctAmt, fixAmt, netAmt, paid,
                balance: Math.round(bal * 100) / 100,
                remark,
                notes: r.description || "",
                payment_mode: r.payment_mode || "",
                _type: r.txn_type,
                // v1.29.15 — surface the audit timestamps so the ledger row
                // and mobile card can render the "Added/Edited …" stamp.
                created_at: r.created_at || null,
                updated_at: r.updated_at || null,
            });
        }
        return out;
    }, [ledger]);

    // v1.29.13 — Newest-first by default, persisted across sessions so a
    // returning party sees their own preferred order. Sort is date-based;
    // ties fall back to the original backend order (which is chronological).
    const [sort, setSort] = useLedgerSortPref({ by: "date", dir: "desc" });
    const sortedRows = useMemo(() => {
        if (!displayRows.length) return displayRows;
        const factor = sort?.dir === "asc" ? 1 : -1;
        const withIdx = displayRows.map((r, i) => ({ r, i }));
        withIdx.sort((a, b) => {
            const ad = new Date(a.r.date || 0).getTime();
            const bd = new Date(b.r.date || 0).getTime();
            if (ad !== bd) return (ad - bd) * factor;
            return (a.i - b.i) * factor;
        });
        return withIdx.map((x) => x.r);
    }, [displayRows, sort]);

    // v1.29.13 — Auto-scroll to the latest entry on first successful load
    // (once per mount). Guarded so 30-second auto-refreshes don't hijack
    // scroll position after the party has scrolled somewhere else.
    const tableTopRef = useRef(null);
    const tableBottomRef = useRef(null);
    const didAutoScrollRef = useRef(false);
    useEffect(() => {
        if (!ledger || !sortedRows.length) return;
        if (didAutoScrollRef.current) return;
        didAutoScrollRef.current = true;
        const timer = setTimeout(() => {
            const target = (sort?.dir === "asc" ? tableBottomRef : tableTopRef).current;
            target?.scrollIntoView({ behavior: "smooth", block: "center" });
        }, 200);
        return () => clearTimeout(timer);
    }, [ledger, sortedRows.length]); // eslint-disable-line react-hooks/exhaustive-deps

    if (!ledger) return <div className="text-slate-500">Loading…</div>;

    return (
        <div className="space-y-6" data-testid="customer-ledger-page">
            <div className="flex flex-wrap items-end justify-between gap-4">
                <div>
                    <div className="text-xs uppercase tracking-[0.2em] text-slate-500">{ledger.party.name}</div>
                    <h1 className="mt-1 font-display text-4xl font-bold tracking-tight text-slate-900">My Ledger</h1>
                    <div className="mt-2">
                        <LastUpdatedChip lastUpdated={lastUpdated} isRefreshing={isRefreshing} onRefresh={refresh} testId="ledger-last-updated" />
                    </div>
                </div>
                <ExportButton
                    endpoint={{ pdf: `/export/ledger/${user.party_id}.pdf`, xlsx: `/export/ledger/${user.party_id}.xlsx` }}
                    filenameBase={`ledger-${(ledger.party.name || "party").replace(/\s+/g, "_")}`}
                    testId="customer-ledger-export"
                    variant="primary"
                    label="Download Statement"
                />
            </div>

            <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
                <Card label="Opening" value={inrCur(Math.abs(ledger.opening_balance || 0))} />
                <Card label="Charge %" value={`${Number(ledger.charge_percent || 0).toFixed(2)} %`} />
                <Card label="Entries" value={sortedRows.length} />
                {/* v1.27.19 — Show absolute value + directional label. On admin
                    view a negative closing_balance means "MD Impex owes party".
                    From the party's OWN perspective that's money coming to
                    them, so we present it as a positive figure with a clear
                    label rather than a scary red minus sign that confused users. */}
                <Card
                    label={
                        Number(ledger.closing_balance || 0) < 0 ? "MD Impex will pay you"
                        : Number(ledger.closing_balance || 0) > 0 ? "You have to pay"
                        : "Settled"
                    }
                    value={inrCur(Math.abs(Number(ledger.closing_balance || 0)))}
                    accent
                    tone={Number(ledger.closing_balance || 0) < 0 ? "emerald" : Number(ledger.closing_balance || 0) > 0 ? "rose" : "slate"}
                />
            </div>

            {/* v1.29.13 — Sort toggle. Persists across sessions. Placed above
                both the mobile cards and the desktop table so party can flip
                order without hunting for column headers. */}
            {displayRows.length > 0 && (
                <div className="flex items-center justify-between gap-2" data-testid="customer-ledger-sort-bar">
                    <div className="text-xs uppercase tracking-[0.15em] text-slate-500">
                        {sortedRows.length} {sortedRows.length === 1 ? "entry" : "entries"}
                    </div>
                    <button
                        type="button"
                        data-testid="customer-ledger-sort-toggle"
                        onClick={() => setSort((s) => ({ by: "date", dir: (s?.dir === "asc") ? "desc" : "asc" }))}
                        title={sort?.dir === "asc" ? "Currently oldest first — click to show newest first" : "Currently newest first — click to show oldest first"}
                        className="inline-flex items-center gap-1.5 rounded-full border border-emerald-300 bg-white px-3 py-1 text-xs font-semibold uppercase tracking-wider text-emerald-800 shadow-sm transition hover:bg-emerald-50"
                    >
                        {sort?.dir === "asc" ? <ArrowUp size={12} weight="bold" /> : <ArrowDown size={12} weight="bold" />}
                        <span>{sort?.dir === "asc" ? "Oldest first" : "Newest first"}</span>
                    </button>
                </div>
            )}

            <div ref={tableTopRef} data-testid="customer-ledger-top-marker" aria-hidden />

            {/* Mobile card layout — hidden on md+ where the desktop table takes over.
                v1.27.46: on phones the 10-column table was clipping every amount past
                the UTR, forcing horizontal scroll. Each txn now renders as a self-
                contained card showing Date, UTR-or-payment-mode chip, Received, Paid,
                Net, Balance and the Approved/Paid status — no side-scroll needed. */}
            <div className="space-y-3 md:hidden" data-testid="ledger-cards-mobile">
                {sortedRows.length === 0 ? (
                    <div className="rounded-lg border border-slate-200 bg-white p-6 text-center text-sm text-slate-400 shadow-sm">
                        No transactions yet
                    </div>
                ) : sortedRows.map((row) => {
                    const isPayout = row._type === "payout";
                    const remarkCls = {
                        APPROVED: "bg-emerald-100 text-emerald-800",
                        REJECTED: "bg-rose-100 text-rose-800",
                        INVOICE: "bg-slate-100 text-slate-700",
                        PAID: "bg-violet-100 text-violet-800",
                    }[row.remark] || (isPayout ? "bg-violet-100 text-violet-800" : "bg-slate-100 text-slate-700");
                    // Show UTR when present; otherwise fall back to the payment mode
                    // (CASH PAID, UPI, CASH etc.) as an uppercase chip instead of a dash.
                    const modeLabel = (row.payment_mode || (isPayout ? "CASH PAID" : "CASH")).toUpperCase();
                    return (
                        <div key={row.id} className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm" data-testid={`ledger-card-${row.id}`}>
                            <div className="flex items-start justify-between gap-3">
                                <div>
                                    <div className="text-xs uppercase tracking-[0.15em] text-slate-500">
                                        {formatDate(row.date)}
                                    </div>
                                    {(row.created_at || row.updated_at) && (
                                        <div
                                            className="mt-0.5 text-[11px] leading-tight"
                                            style={{ color: "#9CA3AF" }}
                                            title={
                                                (row.created_at ? `Created: ${new Date(row.created_at).toLocaleString("en-IN")}\n` : "") +
                                                (row.updated_at && row.updated_at !== row.created_at ? `Last edited: ${new Date(row.updated_at).toLocaleString("en-IN")}` : "")
                                            }
                                            data-testid={`ledger-card-stamp-${row.id}`}
                                        >
                                            {row.updated_at && row.updated_at !== row.created_at ? "Edited " : "Added "}
                                            {(() => {
                                                const ts = (row.updated_at && row.updated_at !== row.created_at) ? row.updated_at : row.created_at;
                                                if (!ts) return "";
                                                const d = new Date(ts);
                                                if (Number.isNaN(d.getTime())) return "";
                                                const p = (n) => String(n).padStart(2, "0");
                                                return `${p(d.getDate())}-${p(d.getMonth() + 1)}-${d.getFullYear()} ${p(d.getHours())}:${p(d.getMinutes())}`;
                                            })()}
                                        </div>
                                    )}
                                    <div className="mt-1">
                                        {row.utr ? (
                                            <span className="font-mono text-sm font-semibold text-slate-800 break-all" data-testid={`ledger-card-utr-${row.id}`}>{row.utr}</span>
                                        ) : (
                                            <span className={`inline-block rounded px-2 py-0.5 text-xs font-semibold uppercase tracking-wider ${isPayout ? "bg-violet-100 text-violet-800" : "bg-amber-100 text-amber-800"}`} data-testid={`ledger-card-mode-${row.id}`}>{modeLabel}</span>
                                        )}
                                    </div>
                                </div>
                                <span className={`inline-block rounded px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wider whitespace-nowrap ${remarkCls}`} data-testid={`ledger-card-remark-${row.id}`}>
                                    {row.remark}
                                </span>
                            </div>
                            <div className="mt-3 grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                                {row.recd > 0 && (
                                    <div>
                                        <div className="text-[11px] uppercase tracking-wider text-slate-500">Received</div>
                                        <div className="tabular-nums font-medium text-slate-900" data-testid={`ledger-card-recd-${row.id}`}>{inr0(row.recd)}</div>
                                    </div>
                                )}
                                {row.paid > 0 && (
                                    <div>
                                        <div className="text-[11px] uppercase tracking-wider text-slate-500">Paid</div>
                                        <div className="tabular-nums font-medium text-rose-700" data-testid={`ledger-card-paid-${row.id}`}>{inr0(row.paid)}</div>
                                    </div>
                                )}
                                {row.pctAmt > 0 && (
                                    <div>
                                        <div className="text-[11px] uppercase tracking-wider text-slate-500">{row.pct ? `Charge ${row.pct.toFixed(2)}%` : "Charge"}</div>
                                        <div className="tabular-nums text-slate-700">{inr0(row.pctAmt)}</div>
                                    </div>
                                )}
                                {row.fixAmt > 0 && (
                                    <div>
                                        <div className="text-[11px] uppercase tracking-wider text-slate-500">Portal fee</div>
                                        <div className="tabular-nums text-amber-800">{inr0(row.fixAmt)}</div>
                                    </div>
                                )}
                                {row.netAmt > 0 && (
                                    <div>
                                        <div className="text-[11px] uppercase tracking-wider text-slate-500">Net</div>
                                        <div className="tabular-nums font-semibold text-slate-900" data-testid={`ledger-card-net-${row.id}`}>{inr0(row.netAmt)}</div>
                                    </div>
                                )}
                                <div className="col-span-2 mt-1 flex items-center justify-between border-t border-slate-100 pt-2">
                                    <div className="text-[11px] uppercase tracking-wider text-slate-500">Balance</div>
                                    <div className={`tabular-nums text-base font-bold ${row.balance < 0 ? "text-emerald-700" : row.balance > 0 ? "text-rose-700" : "text-slate-600"}`} data-testid={`ledger-card-balance-${row.id}`}>
                                        {inr(Math.abs(row.balance))}
                                    </div>
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>

            <div className="hidden md:block rounded-lg border border-slate-200 bg-white shadow-sm">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="ledger-rows">
                        <thead>
                            <tr className="bg-emerald-700 text-left text-xs font-semibold uppercase tracking-wider text-white">
                                <th className="px-4 py-3 border-r border-emerald-600">Date</th>
                                <th className="px-4 py-3 border-r border-emerald-600">UTR Numbers</th>
                                <th className="px-4 py-3 text-right border-r border-emerald-600">RECD IN QR</th>
                                <th className="px-3 py-3 text-right border-r border-emerald-600">%</th>
                                <th className="px-3 py-3 text-right border-r border-emerald-600">% amt</th>
                                <th className="px-4 py-3 text-right border-r border-emerald-600">Net Amt</th>
                                <th className="px-3 py-3 text-right border-r border-emerald-600 whitespace-nowrap">Portal fix<br/>charges</th>
                                <th className="px-4 py-3 text-right border-r border-emerald-600">Amt paid</th>
                                <th className="px-4 py-3 text-right border-r border-emerald-600">Amt Balance</th>
                                <th className="px-4 py-3">Remark</th>
                            </tr>
                        </thead>
                        <tbody>
                            {sortedRows.length === 0 ? (
                                <tr><td colSpan={10} className="px-5 py-10 text-center text-slate-400">No transactions yet</td></tr>
                            ) : sortedRows.map((row) => {
                                const remarkCls = {
                                    APPROVED: "text-emerald-700 font-semibold",
                                    REJECTED: "text-rose-700 font-semibold",
                                    INVOICE: "text-slate-600",
                                    PAID: "text-violet-700 font-semibold",
                                }[row.remark] || (row._type === "payout" ? "text-violet-700 font-semibold" : "text-slate-600");
                                return (
                                    <tr key={row.id} className="border-b border-slate-100 hover:bg-slate-50">
                                        <td className="px-4 py-2.5 text-slate-800 align-top">
                                            <TxnDateCell
                                                date={row.date}
                                                created_at={row.created_at}
                                                updated_at={row.updated_at}
                                                testId={`ledger-row-date-${row.id}`}
                                            />
                                        </td>
                                        <td className="px-4 py-2.5 font-mono text-xs text-slate-700">
                                            {/* v1.27.48 — UTR column never renders a bare dash. If the row
                                                has no UTR/reference we fall back to a coloured chip carrying
                                                the payment mode (or CASH / CASH PAID) so the party can tell
                                                cash rows apart from missing-data rows at a glance. Matches
                                                the admin Reports ledger and the mobile card fallback. */}
                                            {row.utr ? row.utr : row._type === "payout" ? (
                                                <span className="rounded bg-violet-100 px-1.5 py-0.5 uppercase text-violet-800" data-testid={`cust-ledger-payout-mode-${row.id}`}>
                                                    {row.payment_mode ? row.payment_mode : "CASH\u00a0PAID"}
                                                </span>
                                            ) : (row.payment_mode && row.payment_mode.toLowerCase() !== "cash") ? (
                                                <span className="rounded bg-emerald-100 px-1.5 py-0.5 uppercase text-emerald-800" data-testid={`cust-ledger-recd-mode-${row.id}`}>
                                                    {row.payment_mode}
                                                </span>
                                            ) : (
                                                <span className="rounded bg-amber-100 px-1.5 py-0.5 uppercase text-amber-800" data-testid={`cust-ledger-recd-cash-${row.id}`}>
                                                    CASH
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-4 py-2.5 text-right tabular-nums text-slate-900">{inr0(row.recd)}</td>
                                        <td className="px-3 py-2.5 text-right tabular-nums text-slate-700" data-testid={`cust-ledger-pct-${row.id}`}>
                                            {row.pct ? `${row.pct.toFixed(2)}%` : ""}
                                        </td>
                                        <td className="px-3 py-2.5 text-right tabular-nums text-slate-700">{inr0(row.pctAmt)}</td>
                                        <td className="px-4 py-2.5 text-right tabular-nums font-medium text-slate-900">{inr0(row.netAmt)}</td>
                                        <td className="px-3 py-2.5 text-right tabular-nums text-amber-800" data-testid={`cust-ledger-fix-${row.id}`}>{inr0(row.fixAmt)}</td>
                                            <td className="px-4 py-2.5 text-right tabular-nums font-medium text-rose-700">{inr0(row.paid)}</td>
                                        {/* v1.27.19 — Row balance shown as absolute value with tone
                                            based on direction. Negative raw value = MD Impex owes
                                            party → emerald (Cr); positive = party owes → rose (Dr).
                                            v1.27.59 — Debit-side amounts (paid + Dr balance) are
                                            now red across every ledger surface, matching the PDF. */}
                                        <td className={`px-4 py-2.5 text-right tabular-nums font-semibold ${row.balance < 0 ? "text-emerald-700" : row.balance > 0 ? "text-rose-700" : "text-slate-600"}`}>
                                            {inr(Math.abs(row.balance))}
                                        </td>
                                        <td className={`px-4 py-2.5 whitespace-nowrap text-xs uppercase tracking-wide ${remarkCls}`}>{row.remark}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>
            <div ref={tableBottomRef} data-testid="customer-ledger-bottom-marker" aria-hidden />

            {/* v1.29.13 — Floating "Latest" jump button for the customer portal
                ledger. Same behaviour as the admin Party Ledger: scrolls to
                whichever end holds the newest entry once >= 10 rows and the
                user has scrolled away. */}
            <JumpToLatestButton
                direction={sort?.dir === "asc" ? "asc" : "desc"}
                rowCount={sortedRows.length}
                onJump={() => {
                    const target = (sort?.dir === "asc" ? tableBottomRef : tableTopRef).current;
                    target?.scrollIntoView({ behavior: "smooth", block: "center" });
                }}
                testId="customer-ledger-jump-latest"
            />
        </div>
    );
}

function Card({ label, value, accent, tone }) {
    const toneCls = {
        emerald: "text-emerald-700",
        rose: "text-rose-700",
        slate: "text-slate-900",
    }[tone] || (accent ? "text-blue-700" : "text-slate-900");
    return (
        <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
            <div className="text-xs uppercase tracking-[0.2em] text-slate-500">{label}</div>
            <div className={`mt-2 font-display text-xl font-bold tabular-nums ${toneCls}`}>{value}</div>
        </div>
    );
}
